import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Heart, Share2, ArrowLeft, Pause, Play, VolumeX, Volume2, ExternalLink } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

// Persist a per-device anonymous id so likes work without login
const getAnonId = () => {
  let id = localStorage.getItem("jglinks_anon_id");
  if (!id) {
    id = "anon_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("jglinks_anon_id", id);
  }
  return id;
};

const LIKED_KEY = "jglinks_liked_shorts";
const getLocalLiked = () => {
  try { return new Set(JSON.parse(localStorage.getItem(LIKED_KEY) || "[]")); }
  catch { return new Set(); }
};
const setLocalLiked = (set) => {
  localStorage.setItem(LIKED_KEY, JSON.stringify(Array.from(set)));
};

const formatCount = (n) => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(".0", "") + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(".0", "") + "K";
  return String(n || 0);
};

// Single short card in the vertical feed
const ShortCard = ({ short, isActive, muted, onToggleMute, onLikeChange, embedded }) => {
  const videoRef = useRef(null);
  const navigate = useNavigate();
  const [paused, setPaused] = useState(false);
  const [liked, setLiked] = useState(getLocalLiked().has(short.id));
  const [likes, setLikes] = useState(short.likes || 0);
  const [burstKey, setBurstKey] = useState(0); // double-tap heart burst
  const lastTapRef = useRef(0);
  const viewSentRef = useRef(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (isActive) {
      v.play().catch(() => setPaused(true));
      if (!viewSentRef.current) {
        viewSentRef.current = true;
        fetch(`/api/shorts/${short.id}/view`, { method: "POST" }).catch(() => {});
      }
    } else {
      v.pause();
      v.currentTime = 0;
    }
  }, [isActive, short.id]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPaused(false); }
    else { v.pause(); setPaused(true); }
  };

  const triggerLike = async (forceTrue) => {
    const next = forceTrue ? true : !liked;
    if (next !== liked) {
      setLiked(next);
      setLikes((n) => Math.max(0, n + (next ? 1 : -1)));
    }
    const local = getLocalLiked();
    if (next) local.add(short.id); else local.delete(short.id);
    setLocalLiked(local);
    try {
      // For double-tap: send like only if not already liked
      if (forceTrue && liked) return;
      const r = await fetch(`/api/shorts/${short.id}/like`, {
        method: "POST",
        headers: { "X-Anon-Id": getAnonId() },
      });
      const d = await r.json();
      if (r.ok) {
        setLikes(d.likes ?? likes);
        setLiked(!!d.liked);
        const refreshed = getLocalLiked();
        if (d.liked) refreshed.add(short.id); else refreshed.delete(short.id);
        setLocalLiked(refreshed);
        onLikeChange?.(short.id, d.likes ?? likes);
      }
    } catch { /* keep optimistic */ }
  };

  const toggleLike = () => triggerLike(false);

  const handleVideoTap = () => {
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      // double tap -> like + burst
      setBurstKey((k) => k + 1);
      triggerLike(true);
      lastTapRef.current = 0;
    } else {
      lastTapRef.current = now;
      // delay single-tap action so we can detect a possible double-tap
      setTimeout(() => {
        if (lastTapRef.current && Date.now() - lastTapRef.current >= 250) {
          togglePlay();
          lastTapRef.current = 0;
        }
      }, 260);
    }
  };

  const share = async () => {
    const url = `${window.location.origin}/shorts/${short.id}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: short.caption || "Short", url });
        return;
      } catch { /* user cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <div
      className={`relative w-full ${embedded ? "h-full" : "h-[100dvh]"} snap-start bg-black overflow-hidden`}
      data-testid={`short-card-${short.id}`}
    >
      <video
        ref={videoRef}
        src={short.videoUrl}
        poster={short.thumbnailUrl || undefined}
        playsInline
        loop
        muted={muted}
        onClick={handleVideoTap}
        className="w-full h-full object-cover"
      />

      {/* Top gradient — editorial label area */}
      <div className="pointer-events-none absolute top-0 inset-x-0 h-32 bg-gradient-to-b from-black/80 via-black/20 to-transparent" />

      {/* Editorial label top-left */}
      <div className="absolute top-4 left-4 flex items-center gap-2 pointer-events-none">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse-dot" />
        <span className="text-[10px] tracking-[0.3em] uppercase text-white/70 font-medium">
          · Shorts / Live
        </span>
      </div>

      {/* Double-tap heart burst */}
      <AnimatePresence>
        {burstKey > 0 && (
          <motion.div
            key={burstKey}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
          >
            <Heart
              className="w-32 h-32 text-rose-500 animate-heart-burst absolute top-1/2 left-1/2"
              fill="currentColor"
              strokeWidth={0}
              style={{ transform: "translate(-50%, -50%)" }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tap-to-play overlay */}
      {paused && (
        <button
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center bg-black/30"
          aria-label="Reproducir"
        >
          <Play className="w-16 h-16 text-white/90 drop-shadow-2xl" fill="currentColor" />
        </button>
      )}

      {/* Bottom gradient + creator info + caption + CTA */}
      <div className={`absolute left-0 right-0 ${embedded ? "bottom-20" : "bottom-0"} pb-7 pt-24 px-5 bg-gradient-to-t from-black via-black/75 to-transparent`}>
        {/* Editorial creator chip */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.65, 0, 0.35, 1] }}
          className="flex items-center gap-2.5 mb-3"
        >
          <Link
            to={`/${short.creatorUsername}`}
            className="flex items-center gap-2.5 px-2 py-1 rounded-full bg-black/40 backdrop-blur-md border border-white/10 hover:bg-black/60 transition group"
          >
            <img
              src={short.creatorAvatar || "/logo.png"}
              alt={short.creatorName}
              className="w-8 h-8 rounded-full object-cover ring-1 ring-rose-300/40 group-hover:ring-rose-300/80 transition"
            />
            <span className="font-bold text-white text-[13px] pr-1">@{short.creatorUsername}</span>
          </Link>
          <Link
            to={`/${short.creatorUsername}/shorts`}
            className="px-3 py-1.5 rounded-full text-[10px] uppercase font-black tracking-[0.18em] bg-white text-black hover:bg-white/90 transition"
          >
            Ver perfil
          </Link>
        </motion.div>
        {short.caption && (
          <p className="text-white text-[15px] leading-snug mb-2 line-clamp-3 max-w-[78%] font-medium drop-shadow">
            {short.caption}
          </p>
        )}
        {((short.hashtags && short.hashtags.length > 0) || short.niche) && (
          <div className="flex flex-wrap items-center gap-1.5 mb-3 max-w-[78%]">
            {short.niche && (
              <Link
                to={`/nichos`}
                className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.15em] font-black bg-amber-400/15 text-amber-200 border border-amber-300/30 backdrop-blur-sm hover:bg-amber-400/25 transition"
              >
                {short.niche}
              </Link>
            )}
            {(short.hashtags || []).slice(0, 4).map((t) => (
              <Link
                key={t}
                to={`/hashtags?t=${t}`}
                className="text-[12px] font-semibold text-rose-200/90 hover:text-rose-100 drop-shadow"
              >
                #{t}
              </Link>
            ))}
          </div>
        )}
        {short.ctaUrl && (
          <a
            href={short.ctaUrl}
            target={short.ctaUrl.startsWith("http") ? "_blank" : undefined}
            rel="noopener noreferrer"
            data-testid={`short-cta-${short.id}`}
            className="cta-shine relative inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full overflow-hidden bg-gradient-to-r from-rose-500 via-rose-400 to-amber-400 text-black font-black text-[11px] uppercase tracking-[0.18em] shadow-[0_14px_45px_-12px_rgba(244,63,94,0.7)]"
          >
            <ExternalLink className="w-3.5 h-3.5 relative z-10" />
            <span className="relative z-10">{short.ctaLabel || "Ver más"}</span>
            <span className="cta-shine-bar" />
          </a>
        )}
      </div>

      {/* Right action rail */}
      <div className={`absolute right-3.5 flex flex-col items-center gap-5 ${embedded ? "bottom-48" : "bottom-36"}`}>
        <motion.button
          onClick={toggleLike}
          data-testid={`short-like-${short.id}`}
          className="flex flex-col items-center"
          whileTap={{ scale: 0.88 }}
          transition={{ type: "spring", stiffness: 400, damping: 18 }}
        >
          <motion.div
            animate={liked ? { scale: [1, 1.25, 1] } : { scale: 1 }}
            transition={{ duration: 0.35 }}
            className={`w-12 h-12 rounded-full flex items-center justify-center backdrop-blur ${
              liked
                ? "bg-rose-500/95 shadow-[0_0_22px_rgba(244,63,94,0.55)]"
                : "bg-black/45 border border-white/10"
            }`}
          >
            <Heart className={`w-[22px] h-[22px] ${liked ? "text-white" : "text-white/90"}`} fill={liked ? "currentColor" : "none"} />
          </motion.div>
          <span className="text-white text-[11px] font-bold mt-1.5 drop-shadow">{formatCount(likes)}</span>
        </motion.button>
        <motion.button
          onClick={share}
          data-testid={`short-share-${short.id}`}
          className="flex flex-col items-center"
          whileTap={{ scale: 0.88 }}
        >
          <div className="w-12 h-12 rounded-full bg-black/45 border border-white/10 flex items-center justify-center backdrop-blur">
            <Share2 className="w-5 h-5 text-white/90" />
          </div>
          <span className="text-white text-[10px] mt-1.5 drop-shadow tracking-wide">Compartir</span>
        </motion.button>
        <motion.button
          onClick={onToggleMute}
          data-testid={`short-mute-${short.id}`}
          className="flex flex-col items-center"
          whileTap={{ scale: 0.88 }}
        >
          <div className="w-10 h-10 rounded-full bg-black/45 border border-white/10 flex items-center justify-center backdrop-blur">
            {muted ? <VolumeX className="w-4 h-4 text-white/90" /> : <Volume2 className="w-4 h-4 text-white/90" />}
          </div>
        </motion.button>
      </div>

      {/* Top back button — hidden when embedded in home shell */}
      {!embedded && (
        <button
          onClick={() => navigate(-1)}
          data-testid="shorts-back-btn"
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/55 backdrop-blur border border-white/10 flex items-center justify-center text-white hover:bg-black/70 transition"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

// ============== Public Shorts Feed (/shorts) ==============
export const ShortsFeed = ({ embedded = false }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const [muted, setMuted] = useState(true);
  const containerRef = useRef(null);

  const load = useCallback(async (creator) => {
    setLoading(true);
    try {
      const r = await fetch(`/api/shorts/feed?limit=30${creator ? `&creator=${creator}` : ""}`);
      const d = await r.json();
      setItems(d.items || []);
    } catch {
      toast.error("Error cargando shorts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    load(params.get("creator"));
  }, [load]);

  // Track which short is visible via IntersectionObserver
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const cards = Array.from(root.querySelectorAll("[data-short-idx]"));
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((e) => e.isIntersecting);
        if (visible) {
          const idx = parseInt(visible.target.getAttribute("data-short-idx"), 10);
          if (!Number.isNaN(idx)) setActiveIdx(idx);
        }
      },
      { root, threshold: 0.6 }
    );
    cards.forEach((c) => observer.observe(c));
    return () => observer.disconnect();
  }, [items]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-black text-white/60 text-sm">
        Cargando shorts…
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-black text-white px-6 text-center">
        <Helmet><title>Shorts · JGlinks</title></Helmet>
        <h2 className="text-2xl font-bold mb-2">Aún no hay shorts</h2>
        <p className="text-white/60 text-sm mb-6">Pronto vas a ver clips exclusivos de nuestras modelos.</p>
        <Link to="/" className="px-5 py-2.5 rounded-full bg-white text-black font-bold text-sm">
          Volver al inicio
        </Link>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`${embedded ? "h-full" : "h-[100dvh]"} overflow-y-scroll snap-y snap-mandatory bg-black`}
      style={{ scrollbarWidth: "none" }}
      data-testid="shorts-feed"
    >
      {!embedded && <Helmet><title>Shorts · JGlinks</title></Helmet>}
      {items.map((s, i) => (
        <div key={s.id} data-short-idx={i}>
          <ShortCard
            short={s}
            isActive={i === activeIdx}
            muted={muted}
            onToggleMute={() => setMuted((m) => !m)}
            embedded={embedded}
          />
        </div>
      ))}
    </div>
  );
};

// ============== Single Short (/shorts/{id}) - permalink for shares ==============
export const SingleShort = () => {
  const { shortId } = useParams();
  const navigate = useNavigate();
  const [short, setShort] = useState(null);
  const [loading, setLoading] = useState(true);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`/api/shorts/${shortId}`);
        if (r.ok) {
          const d = await r.json();
          setShort({
            ...d,
            creatorName: d.creator?.name || d.creatorUsername,
            creatorAvatar: d.creator?.avatar,
          });
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [shortId]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-black text-white/60 text-sm">
        Cargando…
      </div>
    );
  }
  if (!short) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-black text-white px-6 text-center">
        <h2 className="text-2xl font-bold mb-2">Short no encontrado</h2>
        <button onClick={() => navigate("/shorts")} className="px-5 py-2.5 rounded-full bg-white text-black font-bold text-sm">
          Ver feed
        </button>
      </div>
    );
  }
  return (
    <div className="h-[100dvh] bg-black">
      <Helmet>
        <title>{`@${short.creatorUsername} · Shorts`}</title>
        <meta property="og:title" content={`@${short.creatorUsername} en JGlinks`} />
        <meta property="og:description" content={short.caption || "Mira este short"} />
        {short.thumbnailUrl && <meta property="og:image" content={short.thumbnailUrl} />}
      </Helmet>
      <ShortCard
        short={short}
        isActive
        muted={muted}
        onToggleMute={() => setMuted((m) => !m)}
      />
    </div>
  );
};

// ============== Creator Shorts Profile (/{username}/shorts) ==============
export const CreatorShortsProfile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], creator: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`/api/shorts/by-creator/${username}`);
        const d = await r.json();
        setData(d);
      } finally {
        setLoading(false);
      }
    })();
  }, [username]);

  const totalViews = data.items.reduce((sum, s) => sum + (s.views || 0), 0);
  const totalLikes = data.items.reduce((sum, s) => sum + (s.likes || 0), 0);

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>{`@${username} · Shorts`}</title>
      </Helmet>
      <header className="sticky top-0 z-10 backdrop-blur bg-black/70 border-b border-white/5 px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="text-white/80 hover:text-white"
          data-testid="creator-shorts-back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          <img
            src={data.creator?.avatar || "/logo.png"}
            alt=""
            className="w-9 h-9 rounded-full object-cover ring-2 ring-pink-400/40"
          />
          <div className="min-w-0">
            <p className="font-bold text-sm truncate">{data.creator?.name || username}</p>
            <p className="text-[11px] text-white/50">@{username} · Shorts</p>
          </div>
        </div>
        <Link
          to={`/${username}`}
          className="text-xs px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/15 text-white/80"
        >
          Perfil
        </Link>
      </header>

      <div className="px-4 py-3 flex gap-4 text-xs text-white/60 border-b border-white/5">
        <div><span className="text-white font-bold text-base">{data.items.length}</span> shorts</div>
        <div><span className="text-white font-bold text-base">{formatCount(totalViews)}</span> vistas</div>
        <div><span className="text-white font-bold text-base">{formatCount(totalLikes)}</span> likes</div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-white/50 text-sm">Cargando…</div>
      ) : data.items.length === 0 ? (
        <div className="p-10 text-center text-white/50 text-sm">
          Esta modelo aún no tiene shorts publicados
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-0.5">
          {data.items.map((s) => (
            <Link
              key={s.id}
              to={`/shorts/${s.id}`}
              data-testid={`creator-short-thumb-${s.id}`}
              className="relative aspect-[9/16] bg-zinc-900 overflow-hidden group"
            >
              {s.thumbnailUrl ? (
                <img
                  src={s.thumbnailUrl}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/30 text-xs">
                  ▶
                </div>
              )}
              <div className="absolute bottom-1 left-1.5 text-white text-[10px] font-bold flex items-center gap-2 drop-shadow">
                <span className="flex items-center gap-0.5"><Play className="w-3 h-3" fill="currentColor" />{formatCount(s.views)}</span>
                <span className="flex items-center gap-0.5"><Heart className="w-3 h-3" fill="currentColor" />{formatCount(s.likes)}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

// expose Pause icon unused warning suppression
const _unused = Pause;
export { _unused };
